/// <reference lib="dom" />
import type { Tag, TokenSpec } from '../shared/contract';
import {
  BAKELIT, BLUTROT, CREME, css, INK, NACHT, NEON, PAPIER, SENF,
} from '../render-kit/src/art/palette';

/**
 * META-PROGRESSION — the unlock ladder, Die Vitrine, das Tageslos, the receipt export.
 *
 * Four features, one law: **nothing in this file may make a run stronger.** Every rung of
 * the ladder puts *new things into the pools* (core-loop.md, "Meta-progression"); not one
 * of them raises a number. That is not squeamishness — it is the precondition for the
 * daily seed being worth anything at all. If night 40 started richer than night 1, two
 * players on the same seed would not be playing the same game, and the only interesting
 * question a daily seed asks ("what did *you* do with this room?") would be unanswerable.
 *
 * The same law is why the Gepäck archetypes below are checked arithmetically rather than
 * by eye: each starting bag carries the same token count, the same total Nennwert and the
 * same material-weighted value as Der Hausbeutel (core/sim.ts's default). They change the
 * *shape* of your money, never the amount. test/meta.test.ts asserts this against core's
 * own `MATERIAL_MULT`, so a future archetype cannot quietly smuggle in a stat bonus.
 *
 * What lives here and why:
 *
 *  - `LADDER` / `unlockedIds`  — data, no side effects. The run director feeds the ids
 *    into its pool draw; this module never touches the sim.
 *  - `Vitrine`                 — a DOM object (paper and enamel, ART-BIBLE §10), which
 *    shelves won prizes *and* the red receipts of lost runs. Death is content.
 *  - `dailySeed` / `dailyKey`  — a seed from the UTC date, no wall clock inside the hash.
 *  - `receiptImage`            — the till roll as a PNG, because the anecdote is the
 *    export (core-loop.md: "the receipt is the anecdote-export button").
 *
 * Two structural notes for whoever reads this next:
 *
 *  1. The `/// <reference lib="dom" />` above is deliberate. The root tsconfig has no DOM
 *     lib on purpose (core/ must not compile against `document`), and test/ compiles under
 *     it — so a test that imports this module would otherwise fail to typecheck. The
 *     directive makes the module carry its own DOM requirement instead of loosening a
 *     tsconfig that is doing an important job.
 *  2. Storage never throws. A `localStorage` access can fail for reasons that have nothing
 *     to do with this game (privacy mode, a full quota, a corrupted key from an older
 *     build), and an exception on the boot path would take the whole arcade dark for a
 *     trophy shelf. Every read and write below is wrapped, and every field is coerced.
 */

// ---------------------------------------------------------------- shapes

/** The small set of facts a finished run reports. Populate from the run's chronicle. */
export interface RunSummary {
  /** the run's seed — reproduces the night, and is printed on the receipt image */
  seed: number;
  won: boolean;
  /** deepest floor reached (1..FLOORS) */
  floor: number;
  coins: number;
  tickets: number;
  /** VERDACHT high-water mark */
  verdachtPeak: number;
  bankBreaks: number;
  bossesBeaten: number;
  /** Rütteln count for the whole run */
  shoves: number;
  /** the tags of the best combination — the receipt names it from these */
  bestComboTags: Tag[];
  /** German prize names taken out of the Preistheke */
  prizes: string[];

  // ---- optional facts. A minimal integration can omit them; the rungs that read them
  // simply never fire until they are fed, which is the honest failure mode.
  /** the biggest single payout of the run, in coins */
  bestPayout?: number;
  /** coins of the best combination */
  bestComboValue?: number;
  /** floors finished without a single Rütteln */
  cleanFloors?: number;
  /** most enchantment slots filled on one token */
  maxTagsOnToken?: number;
  /** highest multiple of its own Nennwert any coin reached (faceValue / baseValue) */
  ripest?: number;
  /** ZINKEN prised off a cabinet with a Brecheisen */
  zinkenStolen?: number;
  /** left the Preistheke with nothing — no coins, no prize */
  brokeAtTheke?: boolean;
  /** what Der Croupier took */
  confiscated?: string[];
  /** the printed till roll, so the Vitrine can re-export it as an image */
  receipt?: string[];
  /** set by the shell when this run was played on the day's seed */
  dayKey?: string;
}

/** One prize, shelved. */
export interface Trophy {
  name: string;
  floor: number;
  seed: number;
}

/** One receipt, kept. Red ones (lost runs) are the point (core-loop.md). */
export interface Beleg {
  seed: number;
  won: boolean;
  floor: number;
  coins: number;
  lines: string[];
}

/** One day's best attempt on the day's seed. */
export interface DailyBest {
  key: string;
  seed: number;
  floor: number;
  coins: number;
  won: boolean;
  tries: number;
}

/**
 * Everything that survives a run. **Every numeric field is a counter or a high-water mark
 * and only ever grows** — which is what makes "progress can only unlock more, never fewer"
 * a property of the data rather than a promise in a comment. Unlock tests may only read
 * these fields; the lists below are display material and no rung is allowed to depend on
 * them, because they are capped and therefore not monotone.
 */
export interface Progress {
  /** schema version — see `migrate` */
  v: number;
  runs: number;
  wins: number;
  bestFloor: number;
  bestPayout: number;
  bestCoins: number;
  bankBreaks: number;
  bosses: number;
  prizes: number;
  shoves: number;
  cleanFloors: number;
  maxTags: number;
  ripest: number;
  maxVerdacht: number;
  zinkenStolen: number;
  thekeBusts: number;
  /** the best combination ever named, for the shelf's brass plate */
  bestCombo: { name: string; tags: Tag[]; value: number } | null;
  vitrine: Trophy[];
  belege: Beleg[];
  daily: Record<string, DailyBest>;
  /**
   * **v2 — DIE LEHRNACHT.** The framed receipt from the night the player was taught, or
   * null if they have not been taught (or took the door). Its presence is what stops the
   * Lehrnacht being offered again, and clicking its frame in Die Vitrine replays it.
   *
   * It is deliberately a *separate* field from `belege` rather than the thirteenth red
   * receipt on the shelf: the Belege are the twelve most recent nights and they roll
   * over, and the night you learned to play is not something the shelf should quietly
   * carry away.
   */
  lehrgeld: Lehrgeld | null;
  /**
   * **v2.** Which one-time first-encounter asides this profile has already been told.
   * See `app/erstmals.ts` — once per profile, never per run.
   */
  erstmals: string[];
}

/** The framed receipt from Die Lehrnacht. `skipped` means they took the door. */
export interface Lehrgeld {
  seed: number;
  /** true if they said "Ich kenne mich aus." and never played it */
  skipped: boolean;
  /** game-time seconds the night took, per beat — the clock, kept honest */
  seconds: number;
  lines: string[];
}

export interface Unlock {
  id: string;
  name: string;
  note: string;
  grants: { items?: string[]; gepaeck?: string };
  test: (p: Progress) => boolean;
}

/** A starting bag archetype: a pure token-spec list, nothing else. */
export interface Gepaeck {
  id: string;
  name: string;
  note: string;
  bag: TokenSpec[];
}

/** The tiny slice of `localStorage` this module uses, so it can be injected in tests. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

// ---------------------------------------------------------------- constants

/** One key, namespaced, versioned inside the payload. */
export const STORAGE_KEY = 'spielhoelle:fortschritt';

/** Bump when the shape below changes incompatibly, and extend `migrate`. */
export const PROGRESS_VERSION = 2;

/** How much furniture the shelf keeps before the oldest is quietly carried away. */
const MAX_TROPHIES = 60;
const MAX_BELEGE = 12;

/**
 * The pool you start with on night one: the six common enchantments and the two common
 * charms. It is deliberately a *working* set — a first run has to be able to build
 * something, or the ladder is teaching an empty room.
 */
export const BASE_POOL: readonly string[] = Object.freeze([
  'magnetschuh',
  'geistermuenze',
  'gummigeld',
  'klebriges-pech',
  'spaltmuenze',
  'schwermuenze',
  'zinseszins',
  'das-pendel',
]);

// ---------------------------------------------------------------- Gepäck

/**
 * DECISION — **a Gepäck carries no enchantments.** It would be the easiest thing in the
 * world to hand the unlocked bags a [Zins] coin and call it flavour, and it would be a
 * flat power increase for anyone who has played longer. Enchantments are earned inside a
 * run. A starting bag decides only how your money is *shaped*: one obsidian brick you dare
 * not lose, or ten pieces of change you can spread across five cabinets.
 *
 * The arithmetic every archetype obeys (asserted in test/meta.test.ts):
 *   - 10 tokens, exactly like `defaultBeutel()` in core/sim.ts
 *   - total Nennwert 16
 *   - total Nennwert × MATERIAL_MULT = 38  ← the number that actually pays out
 * The third line is the load-bearing one: payouts run `faceValue × MATERIAL_MULT`, so two
 * bags with the same face value and different materials are *not* equally strong.
 */
const t = (id: number, material: TokenSpec['material'], faceValue: number): TokenSpec =>
  ({ id, material, faceValue, radius: 0.5, tags: [] });

export const GEPAECK: readonly Gepaeck[] = Object.freeze([
  {
    id: 'hausbeutel',
    name: 'Der Hausbeutel',
    note: 'Was das Haus jedem in die Hand drückt. Sechs Kupfer, drei Silber, ein Gold.',
    bag: [
      t(1, 'kupfer', 1), t(2, 'kupfer', 1), t(3, 'kupfer', 1),
      t(4, 'kupfer', 1), t(5, 'kupfer', 1), t(6, 'kupfer', 1),
      t(7, 'silber', 2), t(8, 'silber', 2), t(9, 'silber', 2),
      t(10, 'gold', 4),
    ],
  },
  {
    id: 'wechselgeld',
    name: 'Das Wechselgeld',
    note: 'Kein Kupfer im Beutel. Jede Marke zählt doppelt, keine einzige ist schwer.',
    bag: [
      t(1, 'silber', 2), t(2, 'silber', 2), t(3, 'silber', 2),
      t(4, 'silber', 2), t(5, 'silber', 2), t(6, 'silber', 2),
      t(7, 'silber', 1), t(8, 'silber', 1),
      t(9, 'gold', 1), t(10, 'gold', 1),
    ],
  },
  {
    id: 'schatulle',
    name: 'Die Schatulle',
    note: 'Kleingeld und ein schwarzer Stein. Die halbe Kasse steckt in einer Marke.',
    bag: [
      t(1, 'kupfer', 1), t(2, 'kupfer', 1), t(3, 'kupfer', 1),
      t(4, 'kupfer', 1), t(5, 'kupfer', 1), t(6, 'kupfer', 1),
      t(7, 'silber', 3), t(8, 'silber', 3), t(9, 'silber', 2),
      t(10, 'obsidian', 2),
    ],
  },
  {
    id: 'musterkoffer',
    name: 'Der Musterkoffer',
    note: 'Von jedem Material eines. Ein Vertreterkoffer, kein Vermögen.',
    bag: [
      t(1, 'kupfer', 1), t(2, 'kupfer', 1), t(3, 'kupfer', 1),
      t(4, 'silber', 3), t(5, 'silber', 2), t(6, 'silber', 2),
      t(7, 'silber', 2), t(8, 'silber', 2),
      t(9, 'gold', 1), t(10, 'obsidian', 1),
    ],
  },
  {
    id: 'nachlass',
    name: 'Der Nachlass',
    note: 'Zwei Goldstücke aus einer Erbschaft, der Rest ist Kleingeld.',
    bag: [
      t(1, 'kupfer', 1), t(2, 'kupfer', 1), t(3, 'kupfer', 1),
      t(4, 'silber', 2), t(5, 'silber', 2), t(6, 'silber', 2),
      t(7, 'silber', 2), t(8, 'silber', 2),
      t(9, 'gold', 2), t(10, 'gold', 1),
    ],
  },
]);

/** The bag for an archetype id, copied — the caller owns its tokens. */
export function gepaeckBag(id: string): TokenSpec[] {
  const g = GEPAECK.find((x) => x.id === id) ?? GEPAECK[0];
  return g.bag.map((s) => ({ ...s, tags: [...s.tags] }));
}

// ---------------------------------------------------------------- the ladder

/**
 * FIFTEEN RUNGS. Balatro's lesson is that an unlock has to name a *deed*, not a tally:
 * "play 20 runs" is a receipt for time spent, "burst a bank" is a sentence about something
 * you did, and only the second one makes a player look at the room differently on the way
 * there. Two rungs key off the run count anyway, as pacing — a ladder made entirely of
 * feats has nothing on it for the player who is still learning which lever does what.
 *
 * Every `test` reads only monotone counters (see `Progress`), so unlocking is one-way by
 * construction. Every id in `grants.items` is a real entry in core/items/catalogue.ts —
 * a fabricated id would silently do nothing, so the test file checks all of them against
 * the real catalogue rather than trusting this list.
 */
export const LADDER: Unlock[] = [
  {
    id: 'zweite-nacht',
    name: 'Die zweite Nacht',
    note: 'Eine Nacht hinter sich gebracht — egal wie sie ausging.',
    grants: { items: ['manip:gezinkte-feder'] },
    test: (p) => p.runs >= 1,
  },
  {
    id: 'ruhige-hand',
    name: 'Ruhige Hand',
    note: 'Eine Etage beendet, ohne ein einziges Mal zu rütteln.',
    grants: { items: ['manip:klemmkeil'] },
    test: (p) => p.cleanFloors >= 1,
  },
  {
    id: 'bank-gesprengt',
    name: 'Bank gesprengt',
    note: 'Einen Automaten über seine Struktur hinaus gefüttert, bis er barst.',
    grants: { items: ['manip:bohrloch'] },
    test: (p) => p.bankBreaks >= 1,
  },
  {
    id: 'reif',
    name: 'Reif',
    note: 'Eine Marke auf das Achtfache ihres Nennwerts reifen lassen, ohne sie zu kassieren.',
    grants: { items: ['zinsmuenze'] },
    test: (p) => p.ripest >= 8,
  },
  {
    id: 'etage-drei',
    name: 'Etage drei',
    note: 'Bis in die dritte Etage hinunter.',
    grants: { gepaeck: 'wechselgeld' },
    test: (p) => p.bestFloor >= 3,
  },
  {
    id: 'drei-verzauberungen',
    name: 'Voll belegt',
    note: 'Drei Verzauberungen auf einer einzigen Marke.',
    grants: { items: ['echomuenze'] },
    test: (p) => p.maxTags >= 3,
  },
  {
    id: 'saalchef',
    name: 'Der Saalchef sitzt nicht mehr',
    note: 'Einen Saalchef geschlagen und seine Regel mitgenommen.',
    grants: { items: ['umgekehrte-buchfuehrung'] },
    test: (p) => p.bosses >= 1,
  },
  {
    id: 'grosse-auszahlung',
    name: 'Die große Auszahlung',
    note: 'Über 2.000 Münzen in einer einzigen Auszahlung.',
    grants: { items: ['der-deckel'] },
    test: (p) => p.bestPayout >= 2000,
  },
  {
    id: 'blankgezogen',
    name: 'Blankgezogen',
    note: 'An der Preistheke alles verloren: kein Preis, keine Münze.',
    grants: { items: ['die-quersubvention'] },
    test: (p) => p.thekeBusts >= 1,
  },
  {
    id: 'hausverbot',
    name: 'Hausverbot',
    note: 'VERDACHT auf 90 getrieben und trotzdem weitergespielt.',
    grants: { gepaeck: 'schatulle' },
    test: (p) => p.maxVerdacht >= 90,
  },
  {
    id: 'etage-fuenf',
    name: 'Etage fünf',
    note: 'Bis in die fünfte Etage hinunter. Es riecht anders da unten.',
    grants: { items: ['kassierter-fluch'] },
    test: (p) => p.bestFloor >= 5,
  },
  {
    id: 'achte-nacht',
    name: 'Die achte Nacht',
    note: 'Acht Nächte in diesem Saal. Der Croupier grüßt inzwischen.',
    grants: { gepaeck: 'musterkoffer' },
    test: (p) => p.runs >= 8,
  },
  {
    id: 'erbeutet',
    name: 'Erbeutet',
    note: 'Einen ZINKEN mit dem Brecheisen vom Automaten gehoben.',
    grants: { items: ['manip:magnet-unterm-tisch'] },
    test: (p) => p.zinkenStolen >= 1,
  },
  {
    id: 'die-sammlung',
    name: 'Die Sammlung',
    note: 'Fünf Preise aus der Preistheke geholt. Der Bär passt nicht durch den Schacht.',
    grants: { items: ['der-riesenplueschbaer', 'item:brecheisen'] },
    test: (p) => p.prizes >= 5,
  },
  {
    id: 'sperrstunde',
    name: 'Sperrstunde',
    note: 'Den Marker bezahlt und im Morgengrauen hinausgegangen.',
    grants: { gepaeck: 'nachlass' },
    test: (p) => p.wins >= 1,
  },
];

/**
 * The item ids the pools may draw from, sorted so the run director's shuffle sees a stable
 * input (core/run.ts sorts before it shuffles for exactly the same reason).
 */
export function unlockedIds(p: Progress): string[] {
  const out = new Set<string>(BASE_POOL);
  for (const u of LADDER) {
    if (!u.test(p)) continue;
    for (const id of u.grants.items ?? []) out.add(id);
  }
  return [...out].sort();
}

/** The starting-bag archetypes the player may choose. Der Hausbeutel is always there. */
export function unlockedGepaeck(p: Progress): string[] {
  const out = new Set<string>(['hausbeutel']);
  for (const u of LADDER) {
    if (u.test(p) && u.grants.gepaeck) out.add(u.grants.gepaeck);
  }
  return [...out];
}

/** What crossed the line between two states — the "NEU IM SAAL" toast after a run. */
export function newlyUnlocked(before: Progress, after: Progress): Unlock[] {
  return LADDER.filter((u) => !u.test(before) && u.test(after));
}

// ---------------------------------------------------------------- das Tageslos

/**
 * THE DAILY SEED.
 *
 * **Honest scope, stated once here and again in the UI copy below: this is per-build and
 * local.** Everyone who loads the same build on the same UTC day gets the same seed, and
 * that is all that is claimed. Bit-for-bit equality of a *run* across browsers has not
 * been verified in this project — the sim is deterministic by construction and hashes
 * identically across node versions in test/determinism.test.ts, but Rapier's WASM build
 * running under four different JS engines is not the same experiment. Until somebody runs
 * it, the Tageslos is a shared starting room, not a leaderboard, and the panel says so.
 *
 * No wall clock enters the hash: the seed is a pure function of the UTC calendar date, so
 * a player in Auckland and a player in Los Angeles disagree about *when* the night starts
 * but never about *which* night it is.
 */
export function dailyKey(d: Date = new Date()): string {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  return `${y}-${m < 10 ? '0' : ''}${m}-${day < 10 ? '0' : ''}${day}`;
}

/**
 * FNV-1a, 32-bit, over the date string. Hand-rolled rather than borrowed from core/hash.ts
 * because that hash exists to catch sim drift and is free to change shape; this one is a
 * promise to the player that today's room is today's room.
 */
export function dailySeed(d: Date = new Date()): number {
  const key = dailyKey(d);
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    // the classic FNV prime, written as shifts so it stays in 32-bit land
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  // seed 0 is legal but reads like a bug in a URL, and the sim is happier with a nonzero
  return hash === 0 ? 0x5bf03635 : hash >>> 0;
}

/** The caveat, in the game's voice. Printed in the panel and on daily receipt images. */
export const TAGESLOS_HINWEIS =
  'Das Tageslos läuft nur in diesem Browser und in dieser Fassung. '
  + 'Ob dieselbe Nacht anderswo Marke für Marke gleich abläuft, ist nicht geprüft — '
  + 'es gibt hier also keine Rangliste, nur denselben Saal.';

// ---------------------------------------------------------------- persistence

export function emptyProgress(): Progress {
  return {
    v: PROGRESS_VERSION,
    runs: 0, wins: 0, bestFloor: 0, bestPayout: 0, bestCoins: 0,
    bankBreaks: 0, bosses: 0, prizes: 0, shoves: 0, cleanFloors: 0,
    maxTags: 0, ripest: 0, maxVerdacht: 0, zinkenStolen: 0, thekeBusts: 0,
    bestCombo: null, vitrine: [], belege: [], daily: {},
    lehrgeld: null, erstmals: [],
  };
}

function store(explicit?: StorageLike | null): StorageLike | null {
  if (explicit !== undefined) return explicit;
  try {
    // `globalThis.localStorage` rather than the bare global: node has no such binding at
    // all, and a bare reference would be a ReferenceError instead of `undefined`.
    const g = globalThis as { localStorage?: StorageLike };
    return g.localStorage ?? null;
  } catch {
    // Safari in private mode and a few enterprise policies throw on the *property access*
    return null;
  }
}

const num = (x: unknown, fallback = 0): number =>
  (typeof x === 'number' && Number.isFinite(x) ? x : fallback);

const str = (x: unknown, fallback = ''): string => (typeof x === 'string' ? x : fallback);

const bool = (x: unknown): boolean => x === true;

const arr = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);

/**
 * Migration. The door was built at v1 before there was anything to move through it, and
 * v2 is the first time it earns its keep.
 *
 * **v1 → v2 (LEHRGELD).** Two fields are added and neither may be defaulted naively.
 * `erstmals` is safe as `[]` — the worst case is that a returning player is told once
 * more that a crooked "?" means a rigging, which is a shrug. `lehrgeld` is **not**: it is
 * the flag that decides whether the next boot is the tutorial, and a returning player
 * with forty nights on the shelf must never be sent back to school. So an existing
 * profile that has played at all is migrated with a *stamped* Lehrgeld saying so.
 */
function migrate(raw: Record<string, unknown>): Record<string, unknown> {
  const v = num(raw.v, 0);
  if (v >= PROGRESS_VERSION) return raw;
  const out: Record<string, unknown> = { ...raw, v: PROGRESS_VERSION };
  if (v < 2 && out.lehrgeld == null && num(out.runs) > 0) {
    // they were here before there was a Lehrnacht; they are not a beginner
    out.lehrgeld = { seed: 0, skipped: true, seconds: 0, lines: [] };
  }
  return out;
}

/** Coerce anything into a valid Progress. Unknown, missing and hostile input all work. */
export function normalizeProgress(input: unknown): Progress {
  const p = emptyProgress();
  if (!input || typeof input !== 'object') return p;
  const raw = migrate(input as Record<string, unknown>);

  p.runs = Math.max(0, Math.floor(num(raw.runs)));
  p.wins = Math.max(0, Math.floor(num(raw.wins)));
  p.bestFloor = Math.max(0, Math.floor(num(raw.bestFloor)));
  p.bestPayout = Math.max(0, num(raw.bestPayout));
  p.bestCoins = Math.max(0, num(raw.bestCoins));
  p.bankBreaks = Math.max(0, Math.floor(num(raw.bankBreaks)));
  p.bosses = Math.max(0, Math.floor(num(raw.bosses)));
  p.prizes = Math.max(0, Math.floor(num(raw.prizes)));
  p.shoves = Math.max(0, Math.floor(num(raw.shoves)));
  p.cleanFloors = Math.max(0, Math.floor(num(raw.cleanFloors)));
  p.maxTags = Math.max(0, Math.floor(num(raw.maxTags)));
  p.ripest = Math.max(0, num(raw.ripest));
  p.maxVerdacht = Math.max(0, num(raw.maxVerdacht));
  p.zinkenStolen = Math.max(0, Math.floor(num(raw.zinkenStolen)));
  p.thekeBusts = Math.max(0, Math.floor(num(raw.thekeBusts)));

  const bc = raw.bestCombo;
  if (bc && typeof bc === 'object') {
    const o = bc as Record<string, unknown>;
    p.bestCombo = {
      name: str(o.name, 'EHRLICHE ARBEIT'),
      // tags are checked against the contract's real union, not merely against `string`:
      // a hand-edited key must not be able to put a fake tag on the brass plate
      tags: arr(o.tags).filter((x): x is Tag => typeof x === 'string' && x in TAG_WORD),
      value: num(o.value),
    };
  }

  p.vitrine = arr(raw.vitrine).slice(0, MAX_TROPHIES).map((x) => {
    const o = (x ?? {}) as Record<string, unknown>;
    return { name: str(o.name, 'Etwas Plüschiges'), floor: num(o.floor), seed: num(o.seed) };
  });

  p.belege = arr(raw.belege).slice(0, MAX_BELEGE).map((x) => {
    const o = (x ?? {}) as Record<string, unknown>;
    return {
      seed: num(o.seed),
      won: bool(o.won),
      floor: num(o.floor),
      coins: num(o.coins),
      lines: arr(o.lines).map((l) => str(l)),
    };
  });

  const lg = raw.lehrgeld;
  if (lg && typeof lg === 'object') {
    const o = lg as Record<string, unknown>;
    p.lehrgeld = {
      seed: num(o.seed),
      skipped: bool(o.skipped),
      seconds: Math.max(0, num(o.seconds)),
      lines: arr(o.lines).map((l) => str(l)),
    };
  }

  // ids only, capped: a hand-edited key must not be able to grow without bound, and an
  // unknown id is harmless — it simply matches no aside and is carried along.
  p.erstmals = arr(raw.erstmals).filter((x): x is string => typeof x === 'string').slice(0, 40);

  const d = raw.daily;
  if (d && typeof d === 'object') {
    for (const [key, value] of Object.entries(d as Record<string, unknown>)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key) || !value || typeof value !== 'object') continue;
      const o = value as Record<string, unknown>;
      p.daily[key] = {
        key,
        seed: num(o.seed),
        floor: num(o.floor),
        coins: num(o.coins),
        won: bool(o.won),
        tries: Math.max(1, Math.floor(num(o.tries, 1))),
      };
    }
  }
  return p;
}

/** Read the shelf. Absent, truncated, hand-edited or hostile storage all yield a Progress. */
export function loadProgress(backend?: StorageLike | null): Progress {
  const s = store(backend);
  if (!s) return emptyProgress();
  let text: string | null = null;
  try {
    text = s.getItem(STORAGE_KEY);
  } catch {
    return emptyProgress();
  }
  if (!text) return emptyProgress();
  try {
    return normalizeProgress(JSON.parse(text));
  } catch {
    // A corrupt key is not worth a dialog. The player lost a shelf, not a run — and the
    // next save overwrites it. Deliberately NOT deleted here: a human debugging a bug
    // report wants to see what the broken value actually was.
    return emptyProgress();
  }
}

/** Write the shelf. A full quota or a locked store is a shrug, never an exception. */
export function saveProgress(p: Progress, backend?: StorageLike | null): void {
  const s = store(backend);
  if (!s) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    // QuotaExceededError, or a store that refuses writes. Nothing to do and nothing worth
    // saying: the run already happened and the receipt is already on screen.
  }
}

/**
 * Fold one finished run into the shelf. **Pure**: returns a new Progress and does not read
 * a clock, a store or a global. That is what makes the ladder testable and what lets the
 * shell decide when (and whether) to persist the result.
 */
export function recordRun(p: Progress, s: RunSummary): Progress {
  const next: Progress = {
    ...p,
    v: PROGRESS_VERSION,
    runs: p.runs + 1,
    wins: p.wins + (s.won ? 1 : 0),
    bestFloor: Math.max(p.bestFloor, s.floor),
    bestPayout: Math.max(p.bestPayout, num(s.bestPayout)),
    bestCoins: Math.max(p.bestCoins, s.coins),
    bankBreaks: p.bankBreaks + Math.max(0, s.bankBreaks),
    bosses: p.bosses + Math.max(0, s.bossesBeaten),
    prizes: p.prizes + s.prizes.length,
    shoves: p.shoves + Math.max(0, s.shoves),
    cleanFloors: p.cleanFloors + Math.max(0, num(s.cleanFloors)),
    maxTags: Math.max(p.maxTags, num(s.maxTagsOnToken)),
    ripest: Math.max(p.ripest, num(s.ripest)),
    maxVerdacht: Math.max(p.maxVerdacht, s.verdachtPeak),
    zinkenStolen: p.zinkenStolen + Math.max(0, num(s.zinkenStolen)),
    thekeBusts: p.thekeBusts + (s.brokeAtTheke ? 1 : 0),
    bestCombo: p.bestCombo,
    vitrine: [...p.vitrine],
    belege: [...p.belege],
    daily: { ...p.daily },
  };

  const comboValue = num(s.bestComboValue);
  if (s.bestComboTags.length > 0 && (!p.bestCombo || comboValue > p.bestCombo.value)) {
    next.bestCombo = { name: comboName(s.bestComboTags), tags: [...s.bestComboTags], value: comboValue };
  }

  // the shelf fills from the front, so the newest thing is at eye level
  for (const prize of s.prizes) {
    next.vitrine.unshift({ name: prize, floor: s.floor, seed: s.seed });
  }
  next.vitrine = next.vitrine.slice(0, MAX_TROPHIES);

  if (s.receipt && s.receipt.length > 0) {
    next.belege.unshift({
      seed: s.seed, won: s.won, floor: s.floor, coins: s.coins, lines: [...s.receipt],
    });
    next.belege = next.belege.slice(0, MAX_BELEGE);
  }

  // one best result per day, and only when the shell says this was a Tageslos run
  if (s.dayKey) {
    const prev = next.daily[s.dayKey];
    const tries = (prev?.tries ?? 0) + 1;
    const better = !prev || betterDay(s, prev);
    next.daily[s.dayKey] = better
      ? { key: s.dayKey, seed: s.seed, floor: s.floor, coins: s.coins, won: s.won, tries }
      : { ...prev, tries };
  }
  return next;
}

/** Deeper beats richer, and paying the marker beats both. */
function betterDay(s: RunSummary, prev: DailyBest): boolean {
  if (s.won !== prev.won) return s.won;
  if (s.floor !== prev.floor) return s.floor > prev.floor;
  return s.coins > prev.coins;
}

// ---------------------------------------------------------------- small parts

/** German thousands dots, hand-rolled for the same reason core/run.ts hand-rolls its own:
 *  `toLocaleString` depends on the host's ICU build, and these numbers get screenshotted. */
function fmt(n: number): string {
  const v = Math.round(Math.abs(n)).toString();
  let out = '';
  for (let i = 0; i < v.length; i++) {
    if (i > 0 && (v.length - i) % 3 === 0) out += '.';
    out += v[i];
  }
  return (n < 0 ? '-' : '') + out;
}

const TAG_WORD: Record<Tag, string> = {
  Magnet: 'MAGNET', Geist: 'GEISTER', Gummi: 'GUMMI', Kleber: 'KLEBE', Spalt: 'SPALT',
  Gold: 'GOLD', Fluch: 'FLUCH', Feuer: 'FEUER', Gluecks: 'GLÜCKS', Schwer: 'SCHWER',
  Zins: 'ZINS', Echo: 'ECHO',
};

/**
 * The same naming grammar core/run.ts prints on the receipt, repeated here rather than
 * imported: app/ may import core/, but a trophy plate that changed wording because the
 * sim's receipt formatter was refactored would be a shelf rewriting its own history.
 */
export function comboName(tags: readonly Tag[]): string {
  if (tags.length === 0) return 'EHRLICHE ARBEIT';
  const tail = tags.length >= 3 ? 'KASKADE' : tags.length === 2 ? 'KOMBINATION' : 'ZUG';
  return `${tags.map((x) => TAG_WORD[x]).join('-')}-${tail}`;
}

/** A stable −3.5°..+3.5° from a string, so a shelved object sits where it was put and does
 *  not re-shuffle itself every time the panel opens. Straight rows read as a spreadsheet;
 *  that is the difference between a shelf of objects and a list of strings. */
function tilt(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return ((hash % 71) - 35) / 10;
}

type El = HTMLElement;

const h = (tag: string, cls?: string, text?: string): El => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

// ---------------------------------------------------------------- Die Vitrine

/**
 * The shelf's stylesheet, exported as a string so it can be bundled instead of injected if
 * the app would rather own its CSS. `Vitrine` injects it itself (once, by element id), so
 * the default path needs no wiring. Colours all come from the render kit's palette —
 * ART-BIBLE §1 forbids a hex literal outside it, and a trophy case that drifted off the
 * game's palette would look exactly like the second game the bible warns about.
 */
export const VITRINE_CSS = `
.sh-vitrine {
  position: absolute; inset: 0; display: none; z-index: 20;
  align-items: center; justify-content: center;
  background: ${css(NACHT.void, 0.72)};
  font-family: var(--sign); pointer-events: auto;
}
.sh-vitrine.on { display: flex; }
.sh-vitrine-frame {
  width: min(760px, 92vw); max-height: 86vh; overflow: auto;
  padding: 14px 18px 20px;
  background: ${css(CREME.base)}; color: ${css(INK)};
  border: 2px solid ${css(BAKELIT.base)}; border-bottom-width: 4px;
  box-shadow: 0 6px 0 ${css(NACHT.void, 0.8)};
  transform: scale(0.98);
  transition: transform var(--settle) cubic-bezier(0.34, 1.56, 0.64, 1);
}
.sh-vitrine.on .sh-vitrine-frame { transform: scale(1); }
.sh-vitrine-head {
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
  padding-bottom: 6px; margin-bottom: 10px;
  border-bottom: 2px solid ${css(INK)};
}
.sh-vitrine-title { font-size: 22px; font-weight: 600; letter-spacing: 0.14em; }
.sh-vitrine-sub {
  flex: 1; font-family: var(--mono); font-size: 10px; letter-spacing: 0.06em;
  color: ${css(BAKELIT.base)};
}
.sh-vitrine-close {
  padding: 4px 12px; font-family: var(--sign); font-size: 13px; font-weight: 600;
  letter-spacing: 0.12em;
  background: ${css(INK)}; color: ${css(PAPIER)}; border: 0; border-radius: 2px;
}
.sh-vitrine-sect { margin-top: 12px; }
.sh-vitrine-sect > h3 {
  margin: 0 0 5px; font-size: 11px; font-weight: 600; letter-spacing: 0.2em;
  color: ${css(BAKELIT.base)};
}
.sh-vitrine-empty { font-size: 13px; font-style: italic; color: ${css(BAKELIT.base)}; }

/* ---- the shelf itself: objects standing on a board, not a grid of cards */
.sh-vitrine-board {
  display: flex; flex-wrap: wrap; gap: 7px; align-items: flex-end;
  padding: 10px 8px 8px;
  background: ${css(CREME.lit)};
  border: 1px solid ${css(CREME.shade)};
  border-bottom: 4px solid ${css(BAKELIT.base)};
}
.sh-vitrine-preis {
  display: flex; flex-direction: column; min-width: 96px; max-width: 150px;
  padding: 6px 8px 5px;
  background: ${css(SENF.base)}; color: ${css(INK)};
  border: 1px solid ${css(BAKELIT.shade)}; border-radius: 2px;
  box-shadow: 2px 3px 0 ${css(NACHT.void, 0.45)};
}
.sh-vitrine-preis-name { font-size: 13px; font-weight: 600; line-height: 1.15; }
.sh-vitrine-preis-sub {
  font-family: var(--mono); font-size: 8.5px; letter-spacing: 0.06em;
  color: ${css(BAKELIT.shade)};
}
.sh-vitrine-plakette {
  width: 100%; margin-top: 8px; padding: 5px 8px;
  background: ${css(BAKELIT.base)}; color: ${css(CREME.lit)};
  border: 1px solid ${css(BAKELIT.shade)};
  font-family: var(--mono); font-size: 10px; letter-spacing: 0.08em;
}

/* ---- the receipts. Losses furnish the shelf too. */
.sh-vitrine-belege { display: flex; gap: 8px; flex-wrap: wrap; }
.sh-vitrine-beleg {
  width: 160px; padding: 9px 10px 16px;
  background: ${css(PAPIER)}; color: ${css(INK)};
  font-family: var(--mono); font-size: 9.5px; line-height: 1.4;
  box-shadow: 1px 2px 0 ${css(NACHT.void, 0.4)};
  clip-path: polygon(0 0, 100% 0, 100% calc(100% - 6px), 88% 100%, 76% calc(100% - 6px),
    64% 100%, 52% calc(100% - 6px), 40% 100%, 28% calc(100% - 6px), 16% 100%, 4% calc(100% - 6px), 0 100%);
}
.sh-vitrine-beleg.lost { color: ${css(BLUTROT.base)}; }
.sh-vitrine-beleg-kopf {
  font-family: var(--sign); font-size: 12px; font-weight: 600; letter-spacing: 0.1em;
  border-bottom: 1px solid ${css(CREME.shade)}; margin-bottom: 3px; padding-bottom: 2px;
}
.sh-vitrine-beleg pre { margin: 0; white-space: pre-wrap; word-break: break-word; }
.sh-vitrine-bild {
  margin-top: 5px; padding: 3px 6px; width: 100%;
  font-family: var(--sign); font-size: 10px; letter-spacing: 0.1em; font-weight: 600;
  background: ${css(INK)}; color: ${css(PAPIER)}; border: 0; border-radius: 2px;
}

/* ---- the ladder, printed like the Hausregeln board */
.sh-vitrine-rung {
  display: flex; gap: 8px; align-items: baseline;
  padding: 3px 6px; margin: 2px 0; font-size: 13px; line-height: 1.25;
  border-left: 3px solid ${css(NEON.gruen.core)};
  background: ${css(CREME.lit, 0.7)};
}
.sh-vitrine-rung.locked {
  border-left-color: ${css(CREME.shade)}; font-style: italic; color: ${css(BAKELIT.base)};
  background: transparent;
}
.sh-vitrine-rung-n { font-family: var(--mono); font-size: 10px; min-width: 16px; color: ${css(BAKELIT.base)}; }
.sh-vitrine-rung-name { font-weight: 600; min-width: 190px; }
.sh-vitrine-rung-note { flex: 1; }
.sh-vitrine-rung-gift {
  font-family: var(--mono); font-size: 9px; letter-spacing: 0.06em; text-align: right;
  color: ${css(BAKELIT.base)};
}

/* ---- das Lehrgeld: a framed receipt on the wall, not an object on the shelf */
.sh-lehrgeld {
  display: block; width: 100%; text-align: left; cursor: pointer;
  padding: 10px 12px 8px;
  background: ${css(PAPIER)};
  border: 6px solid ${css(BAKELIT.base)};
  outline: 1px solid ${css(BAKELIT.shade)};
  box-shadow: 0 6px 0 ${css(NACHT.deep)}, inset 0 0 0 1px ${css(CREME.shade)};
  transform: rotate(-0.6deg);
}
.sh-lehrgeld:hover:not(:disabled) { transform: rotate(0deg) translateY(-2px); }
.sh-lehrgeld:disabled { cursor: default; opacity: 0.75; }
.sh-lehrgeld.skipped { filter: saturate(0.5); }
.sh-lehrgeld-kopf {
  font-family: var(--sign); font-size: 12px; letter-spacing: 0.14em;
  color: ${css(BAKELIT.shade)}; border-bottom: 1px dashed ${css(CREME.shade)};
  padding-bottom: 4px; margin-bottom: 6px;
}
.sh-lehrgeld pre {
  font-family: var(--mono); font-size: 10.5px; line-height: 1.5;
  color: ${css(INK)}; margin: 0; white-space: pre-wrap;
}
.sh-lehrgeld-fuss {
  margin-top: 6px; font-family: var(--mono); font-size: 9.5px;
  color: ${css(BAKELIT.base)}; letter-spacing: 0.04em;
}

/* ---- das Tageslos */
.sh-vitrine-tag {
  padding: 8px 10px;
  background: ${css(CREME.lit)}; border: 1px dashed ${css(CREME.shade)};
  font-family: var(--mono); font-size: 11px; line-height: 1.45;
}
.sh-vitrine-tag b { font-size: 13px; letter-spacing: 0.06em; }
.sh-vitrine-tag .warn {
  display: block; margin-top: 4px; font-size: 9.5px; color: ${css(BLUTROT.base)};
}
`;

const STYLE_ID = 'sh-vitrine-style';

/** Idempotent: injects the shelf's stylesheet once per document. */
export function injectVitrineCss(doc: Document = document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = VITRINE_CSS;
  doc.head.appendChild(style);
}

export interface VitrineOpts {
  root: El;
  progress?: Progress;
  /** injected so the panel can also be driven from a test page */
  onExport?: (b: Beleg) => void;
  /** clicking the framed LEHRGELD receipt plays Die Lehrnacht again */
  onLehrnacht?: () => void;
}

/**
 * DIE VITRINE — the trophy shelf.
 *
 * It is a *cabinet*, not an overlay: a creme panel with a bakelite frame, a shelf board
 * with a bottom edge thick enough to read as wood, and objects that sit on it at slightly
 * different angles. The angles are hashed from each object's name, so a prize stands where
 * it was put and does not shuffle itself every time the panel opens.
 *
 * It shelves the red receipts as well. core-loop.md is explicit that death is content, and
 * a shelf that only showed victories would quietly turn a game about owing money into a
 * game about winning it.
 */
export class Vitrine {
  readonly el: El;
  private readonly frame: El;
  private readonly body: El;
  private readonly onExport: (b: Beleg) => void;
  private readonly onLehrnacht: (() => void) | null;
  private p: Progress;
  private open_ = false;

  constructor(o: VitrineOpts) {
    injectVitrineCss(o.root.ownerDocument ?? document);
    this.p = o.progress ?? emptyProgress();
    this.onLehrnacht = o.onLehrnacht ?? null;
    this.onExport = o.onExport ?? ((b) => void downloadReceipt(b.lines, { won: b.won, seed: b.seed }));

    this.el = h('div', 'sh-vitrine');
    this.frame = h('div', 'sh-vitrine-frame');
    const head = h('div', 'sh-vitrine-head');
    head.append(
      h('span', 'sh-vitrine-title', 'DIE VITRINE'),
      h('span', 'sh-vitrine-sub', 'Was Sie mitgenommen haben — und was Sie dagelassen haben.'),
    );
    const close = h('button', 'sh-vitrine-close', 'SCHLIESSEN') as HTMLButtonElement;
    close.addEventListener('click', () => this.close());
    head.appendChild(close);

    this.body = h('div', 'sh-vitrine-body');
    this.frame.append(head, this.body);
    this.el.appendChild(this.frame);
    // click the dark outside to close; clicks inside the frame must not fall through
    this.el.addEventListener('click', (ev) => {
      if (ev.target === this.el) this.close();
    });
    o.root.appendChild(this.el);
    this.render();
  }

  get isOpen(): boolean {
    return this.open_;
  }

  update(p: Progress): void {
    this.p = p;
    this.render();
  }

  open(): void {
    this.open_ = true;
    this.el.classList.add('on');
  }

  close(): void {
    this.open_ = false;
    this.el.classList.remove('on');
  }

  toggle(): boolean {
    if (this.open_) this.close();
    else this.open();
    return this.open_;
  }

  destroy(): void {
    this.el.remove();
  }

  // ------------------------------------------------------------------ rendering

  private render(): void {
    const p = this.p;
    this.body.innerHTML = '';
    this.body.append(
      this.shelf(p),
      this.lehrgeld(p),
      this.belege(p),
      this.ladder(p),
      this.tageslos(p),
    );
  }

  /**
   * DAS LEHRGELD — the framed receipt from the night the player was taught.
   *
   * It hangs on the wall rather than standing on the shelf, and it is the one object in
   * this cabinet that is also a door: clicking the frame plays that night again. That is
   * the whole replay mechanism for the tutorial, and it is deliberately the *only* one —
   * there is no menu item called "replay tutorial" anywhere in this game, because there
   * is no menu, and because a framed receipt on the wall of your own trophy cabinet is a
   * more honest place to keep the night you paid for your education.
   */
  private lehrgeld(p: Progress): El {
    const s = this.section('DAS LEHRGELD');
    const lg = p.lehrgeld;
    if (!lg) {
      s.appendChild(h('div', 'sh-vitrine-empty', 'Die Lehrnacht steht noch aus.'));
      return s;
    }
    const frame = h('button', 'sh-lehrgeld') as HTMLButtonElement;
    frame.classList.toggle('skipped', lg.skipped);
    const pre = h('pre');
    pre.textContent = lg.skipped
      ? ['LEHRGELD', '', '„Ich kenne mich aus.“', '', 'Nicht bezahlt.'].join('\n')
      : lg.lines.slice(0, 7).join('\n');
    frame.append(
      h('div', 'sh-lehrgeld-kopf', 'DIE LEHRNACHT'),
      pre,
      h('div', 'sh-lehrgeld-fuss', lg.skipped
        ? 'Nochmal ansehen'
        : `${Math.round(lg.seconds / 60)} Min · Saat ${lg.seed} · nochmal ansehen`),
    );
    frame.disabled = !this.onLehrnacht;
    frame.addEventListener('click', () => this.onLehrnacht?.());
    s.appendChild(frame);
    return s;
  }

  private section(title: string): El {
    const s = h('section', 'sh-vitrine-sect');
    s.appendChild(h('h3', undefined, title));
    return s;
  }

  private shelf(p: Progress): El {
    const s = this.section('DAS REGAL');
    const board = h('div', 'sh-vitrine-board');
    if (p.vitrine.length === 0) {
      board.appendChild(h('div', 'sh-vitrine-empty', 'Noch nichts erbeutet. Die Preistheke wartet.'));
    }
    for (const trophy of p.vitrine) {
      const item = h('div', 'sh-vitrine-preis');
      item.style.transform = `rotate(${tilt(trophy.name + trophy.seed)}deg)`;
      item.append(
        h('span', 'sh-vitrine-preis-name', trophy.name),
        h('span', 'sh-vitrine-preis-sub', `Etage ${trophy.floor} · Saat ${trophy.seed}`),
      );
      board.appendChild(item);
    }
    s.appendChild(board);

    const plate = h('div', 'sh-vitrine-plakette');
    plate.textContent = p.bestCombo
      ? `BESTE KOMBINATION: ${p.bestCombo.name} · ${fmt(p.bestCombo.value)} Münzen`
      : 'BESTE KOMBINATION: keine der Rede wert';
    s.appendChild(plate);

    const stats = h('div', 'sh-vitrine-plakette');
    stats.textContent = `${fmt(p.runs)} Nächte · ${fmt(p.wins)} bezahlt · tiefste Etage ${p.bestFloor}`
      + ` · ${fmt(p.bankBreaks)} Banken gesprengt · VERDACHT-Höchststand ${Math.round(p.maxVerdacht)}`;
    s.appendChild(stats);
    return s;
  }

  private belege(p: Progress): El {
    const s = this.section('DIE BELEGE');
    const row = h('div', 'sh-vitrine-belege');
    if (p.belege.length === 0) {
      row.appendChild(h('div', 'sh-vitrine-empty', 'Noch keine Nacht abgerechnet.'));
    }
    for (const b of p.belege) {
      const slip = h('div', 'sh-vitrine-beleg');
      slip.classList.toggle('lost', !b.won);
      slip.appendChild(h('div', 'sh-vitrine-beleg-kopf', b.won ? 'BEZAHLT' : 'KASSIERT'));
      const pre = h('pre');
      // three lines is the whole slip: enough to remember the night, not enough to re-read
      // it. The full roll is kept in `lines` and comes back out through ALS BILD.
      pre.textContent = [
        `Etage ${b.floor}`,
        `${fmt(b.coins)} Münzen`,
        `Saat ${b.seed}`,
      ].join('\n');
      slip.appendChild(pre);
      const btn = h('button', 'sh-vitrine-bild', 'ALS BILD') as HTMLButtonElement;
      btn.addEventListener('click', () => this.onExport(b));
      slip.appendChild(btn);
      row.appendChild(slip);
    }
    s.appendChild(row);
    return s;
  }

  private ladder(p: Progress): El {
    const done = LADDER.filter((u) => u.test(p)).length;
    const s = this.section(`DER SAAL ÖFFNET SICH — ${done} / ${LADDER.length}`);
    LADDER.forEach((u, i) => {
      const got = u.test(p);
      const row = h('div', 'sh-vitrine-rung');
      row.classList.toggle('locked', !got);
      const gift = u.grants.gepaeck
        ? `GEPÄCK · ${GEPAECK.find((g) => g.id === u.grants.gepaeck)?.name ?? u.grants.gepaeck}`
        : (u.grants.items ?? []).join(' · ');
      row.append(
        h('span', 'sh-vitrine-rung-n', String(i + 1)),
        h('span', 'sh-vitrine-rung-name', u.name),
        h('span', 'sh-vitrine-rung-note', u.note),
        h('span', 'sh-vitrine-rung-gift', got ? gift : 'verschlossen'),
      );
      s.appendChild(row);
    });
    return s;
  }

  private tageslos(p: Progress): El {
    const s = this.section('DAS TAGESLOS');
    const key = dailyKey();
    const best = p.daily[key];
    const box = h('div', 'sh-vitrine-tag');
    const line = h('b', undefined, `${key} · Saat ${dailySeed()}`);
    box.appendChild(line);
    box.appendChild(h('div', undefined, best
      ? `Ihr bestes Ergebnis heute: Etage ${best.floor}, ${fmt(best.coins)} Münzen, `
        + `${best.won ? 'bezahlt' : 'kassiert'} — ${fmt(best.tries)} Versuch(e).`
      : 'Heute noch nicht gespielt.'));
    const warn = h('span', 'warn', TAGESLOS_HINWEIS);
    box.appendChild(warn);
    s.appendChild(box);
    return s;
  }
}

// ---------------------------------------------------------------- the receipt, as a picture

export interface ReceiptImageOpts {
  won: boolean;
  seed: number;
  /** set when the run was played on the day's seed — adds the date and the caveat */
  dayKey?: string;
  /** device scale. 2 is the bible's baked-type default (§5). */
  scale?: number;
}

const ROLL_W = 372;          // matches .sh-receipt in app/style.css
const PAD = 26;
const LINE_H = 18;
const FONT_PX = 13;
const TEAR = 9;              // the same 9 px notch the DOM receipt is clipped with

/** The receipt's own voice: Share Tech Mono, with fallbacks that will not crash a headless
 *  browser or a canvas asked to draw before the OFL font has finished loading. */
const MONO = "'Share Tech Mono', ui-monospace, 'DejaVu Sans Mono', monospace";
const SIGN = "'Barlow Condensed', 'Arial Narrow', sans-serif";

/**
 * THE RECEIPT AS A SHAREABLE IMAGE.
 *
 * A real till roll, not a screenshot of a div: `papier` ground (never white — ART-BIBLE
 * §1.5), monospaced ink, a torn bottom edge, and the seed printed large enough to read off
 * a phone. The seed is the point — the picture *is* the reproduction instructions, so an
 * anecdote can be handed to somebody else as a room they can walk into.
 *
 * A lost run gets the red second ribbon and the KASSIERT overprint, tilted −11° to match
 * the crooked "?" stamp the Hausregeln board uses. The overprint sits *over* the type,
 * because that is what an overprint is.
 */
export function receiptImage(lines: string[], opts: ReceiptImageOpts): HTMLCanvasElement {
  const scale = Math.max(1, opts.scale ?? 2);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const foot = footLines(opts);
  // +1 for the blank line the dashed rule sits on. The paper then ends exactly one PAD
  // below the last line of type, plus the tear — a till roll, not a poster with a margin.
  const total = lines.length + foot.length + 1;
  const cssH = PAD * 2 + total * LINE_H + TEAR;
  canvas.width = Math.round(ROLL_W * scale);
  canvas.height = Math.round(cssH * scale);
  if (!ctx) return canvas; // a context can be refused (lost GPU, blocked canvas); do not crash

  ctx.scale(scale, scale);
  ctx.textBaseline = 'top';

  // ---- the paper, torn along the bottom
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(ROLL_W, 0);
  ctx.lineTo(ROLL_W, cssH - TEAR);
  const notches = 13;
  for (let i = notches; i >= 0; i--) {
    const x = (ROLL_W * i) / notches;
    ctx.lineTo(x, cssH - (i % 2 === 0 ? 0 : TEAR));
  }
  ctx.closePath();
  ctx.fillStyle = css(PAPIER);
  ctx.fill();

  // ---- the type. A lost run prints in the red ribbon, exactly like .sh-receipt.lost.
  const ink = opts.won ? css(INK) : css(BLUTROT.shade);
  ctx.fillStyle = ink;
  ctx.font = `${FONT_PX}px ${MONO}`;
  let y = PAD;
  for (const line of lines) {
    ctx.fillText(line, PAD, y);
    y += LINE_H;
  }

  // ---- the reproduction block: seed first, because it is the only part that is machinery
  y += LINE_H;
  ctx.strokeStyle = css(INK, 0.55);
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 3]);
  ctx.beginPath();
  ctx.moveTo(PAD, y - LINE_H / 2);
  ctx.lineTo(ROLL_W - PAD, y - LINE_H / 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = css(INK);
  for (const line of foot) {
    ctx.fillText(line, PAD, y);
    y += LINE_H;
  }

  if (!opts.won) overprint(ctx, cssH);
  return canvas;
}

/** Seed, link and (for a Tageslos run) the honesty note, wrapped to the roll's width. */
function footLines(opts: ReceiptImageOpts): string[] {
  const out = [`SAAT  ${opts.seed}`, `?seed=${opts.seed}`];
  if (opts.dayKey) {
    out.push(`TAGESLOS  ${opts.dayKey}`);
    out.push(...wrap(TAGESLOS_HINWEIS, 42));
  }
  return out;
}

function wrap(text: string, cols: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    if (line.length > 0 && line.length + word.length + 1 > cols) {
      out.push(line);
      line = word;
    } else line = line.length > 0 ? `${line} ${word}` : word;
  }
  if (line) out.push(line);
  return out;
}

/** KASSIERT, stamped over the type in the red ribbon. */
function overprint(ctx: CanvasRenderingContext2D, cssH: number): void {
  ctx.save();
  // half way down the roll: high enough to hit the body of the receipt, low enough not to
  // land on the printed verdict line, which already says KASSIERT in the till's own words
  ctx.translate(ROLL_W / 2, cssH * 0.5);
  ctx.rotate((-11 * Math.PI) / 180);
  ctx.globalAlpha = 0.82;
  ctx.strokeStyle = css(BLUTROT.base);
  ctx.fillStyle = css(BLUTROT.base);
  ctx.lineWidth = 3;
  ctx.strokeRect(-108, -26, 216, 52);
  ctx.font = `600 34px ${SIGN}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('KASSIERT', 0, 2);
  ctx.restore();
}

/**
 * Save the picture. Awaits the font loader if the browser has one — the receipt is the
 * thing people post, and posting it in a fallback monospace because the OFL face was still
 * three hundred milliseconds away would be a silly way to lose the game's own voice. If
 * the loader is missing, refuses or hangs, we draw anyway: an ugly receipt beats none.
 */
export async function downloadReceipt(lines: string[], opts: ReceiptImageOpts): Promise<void> {
  try {
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (fonts?.ready) await fonts.ready;
  } catch {
    // no font loader, or it rejected. Draw with the fallback stack.
  }
  const canvas = receiptImage(lines, opts);
  let url = '';
  try {
    url = canvas.toDataURL('image/png');
  } catch {
    return; // a tainted or oversized canvas; nothing to hand over
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = `spielhoelle-${opts.seed}-${opts.won ? 'bezahlt' : 'kassiert'}.png`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
